import React, { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useS4LContext } from '@s4l/react-context'
import { useClickAway } from 'react-use'
import { differenceInCalendarDays, differenceInCalendarMonths } from 'date-fns'
import { useDevice } from 'src/hooks/useDevice'
import { useMarket } from 'src/hooks/useMarket'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'

import IconClose from 'client/icons/Close'
import { getWeekDays } from 'client/utils'
import DateIntl from 'src/comps/DateIntl'
import {
  DATE_INTL_FORMAT_WEEKDAY_MONTH_SHORT_TEXT,
  formatMappings,
} from 'src/comps/DateIntl/constants'

import Calendar from './Calendar'
import messages from './messages'
import MobileDates from './MobileDates'

import { ButtonPrimary } from 'src/comps/Button/styles'

import {
  ButtonClose,
  CalendarWrapper,
  DateLine,
  DatePickerBase,
  DatePickerDesktopContainer,
  DatePickerMobileContainer,
  DatesResume,
  Header,
  Ready,
  TopDate,
  TopDates,
  WeekDays,
} from './styles'

const DatePicker = ({
  checkIn,
  checkOut,
  initialFocusedInput = 'checkin',
  onChangeDates,
  onClose,
  dataTestId,
  market,
  type,
  currentDate = new Date(),
  unFreezeScroll = false,
}) => {
  const { i18n } = useS4LContext()
  const { device } = useDevice()
  const { minBookingDate, calendar } = useMarket()
  const containerElement = useRef()

  const initialSelectedDates = useRef({
    startDate: checkIn,
    endDate: checkOut,
  })

  const initialMonthIndex = differenceInCalendarMonths(checkIn, currentDate)

  const [visibleMonths, setVisibleMonths] = useState([initialMonthIndex, initialMonthIndex + 1])
  const [selectedDates, setSelectedDates] = useState(initialSelectedDates.current)
  const [dateFocused, setDateFocused] = useState(initialFocusedInput)
  const prevNights = differenceInCalendarDays(checkOut, checkIn)
  const nights = differenceInCalendarDays(selectedDates.endDate, selectedDates.startDate)
  const nightsTranslation = checkOut
    ? { days: nights || prevNights }
    : { nights: nights || prevNights || 0 }

  const weekDays = getWeekDays(calendar.weekStartsOn)

  function handleDatesChange({ startDate: start, endDate: end }) {
    setSelectedDates({ startDate: start, endDate: end })
    onChangeDates({ checkIn: start, checkOut: end })
  }

  function internalClose() {
    if (!unFreezeScroll) enableBodyScroll(containerElement.current)
    onClose()
  }

  function handleDatesChangeAndClose({ startDate: start, endDate: end, isCheckIn }) {
    const targetFocus = isCheckIn ? 'checkin' : 'checkout'

    setSelectedDates({ startDate: start, endDate: end })
    setDateFocused(targetFocus)
    onChangeDates({ checkIn: start, checkOut: end })

    if (isCheckIn) {
      if (device === 'desktop') {
        disableBodyScroll(containerElement.current)
      }
      internalClose()
    }
  }

  function resetCalendar() {
    if (device === 'desktop') {
      disableBodyScroll(containerElement.current)
    }
    handleDatesChange(initialSelectedDates.current)
    internalClose()
  }

  function navigateMonths(direction) {
    const updatedMonths = visibleMonths.map(month => (direction === 'prev' ? month - 1 : month + 1))

    setVisibleMonths(updatedMonths)
  }

  function handleFocusInput(input) {
    if (!input) return
    setDateFocused(input)
  }

  function closeCalendar() {
    if (device === 'desktop') {
      disableBodyScroll(containerElement.current)
    }
    if (selectedDates.startDate && selectedDates.endDate) {
      onChangeDates({ checkIn: selectedDates.startDate, checkOut: selectedDates.endDate })
    }

    internalClose()
  }

  useClickAway(containerElement, resetCalendar)

  const checkInFormatted = i18n.formatDate(checkIn, formatMappings.DATE_INTL_FORMAT_TWO_DIGITS)
  const checkOutFormatted = i18n.formatDate(checkOut, formatMappings.DATE_INTL_FORMAT_TWO_DIGITS)
  const nightsMessage = i18n.formatMessage(messages.nights, { nights: nights || prevNights || 0 })

  return (
    <DatePickerBase ref={containerElement} data-testid={dataTestId} type={type}>
      <Header>
        <ButtonClose
          id="button-close-date-picker"
          data-testid="button-close-date-picker"
          onClick={closeCalendar}
        >
          <IconClose />
        </ButtonClose>

        <TopDates>
          <TopDate
            active={dateFocused === 'checkin'}
            onClick={() => handleFocusInput('checkin')}
            data-testid="top-dates-check-in"
            className={dateFocused === 'checkin' ? 'active' : ''}
          >
            {i18n.formatMessage(messages.checkIn)}
            <DateLine>
              <DateIntl value={checkIn} format={DATE_INTL_FORMAT_WEEKDAY_MONTH_SHORT_TEXT} />
            </DateLine>
          </TopDate>

          <TopDate
            active={dateFocused === 'checkout'}
            onClick={() => handleFocusInput('checkout')}
            data-testid="top-dates-check-out"
            className={dateFocused === 'checkout' ? 'active' : ''}
          >
            {i18n.formatMessage(messages.checkOut)}
            <DateLine>
              {checkOut ? (
                <DateIntl value={checkOut} format={DATE_INTL_FORMAT_WEEKDAY_MONTH_SHORT_TEXT} />
              ) : (
                '-'
              )}
            </DateLine>
          </TopDate>
        </TopDates>
      </Header>
      <WeekDays id="small" data-testid="week-days-small">
        {weekDays.map(day => (
          <li key={day}>{i18n.formatDate(day, { weekday: 'narrow' })}</li>
        ))}
      </WeekDays>
      <CalendarWrapper>
        <DatePickerDesktopContainer data-testid="voucher-date-picker">
          {visibleMonths.map((month, index) => (
            <Calendar
              index={month}
              isFocusedCheckIn={dateFocused === 'checkin'}
              key={month}
              onNavigate={navigateMonths}
              onSelect={handleDatesChangeAndClose}
              position={index}
              selectedDates={selectedDates}
              variant="horizontal"
              market={market}
              currentDate={currentDate}
              minBookingDate={minBookingDate}
            />
          ))}
        </DatePickerDesktopContainer>
        <DatePickerMobileContainer data-testid="voucher-date-picker-mobile">
          <MobileDates
            endDate={checkOut}
            focusedInput={dateFocused === 'checkin'}
            setDateFocused={setDateFocused}
            dateFocused={dateFocused}
            startDate={checkIn}
            onDatesSelected={handleDatesChange}
            market={market}
            currentDate={currentDate}
            minBookingDate={minBookingDate}
          />
        </DatePickerMobileContainer>
      </CalendarWrapper>
      <DatesResume>
        {`${checkInFormatted} · ${checkOutFormatted} `}
        <strong>{`(${nightsMessage})`}</strong>
      </DatesResume>
      <Ready>
        <ButtonPrimary
          type="button"
          onClick={closeCalendar}
          disabled={!checkOut}
          data-testid="btn-ready"
        >
          {i18n.formatMessage(messages[checkOut ? 'ready' : 'nights'], nightsTranslation)}
        </ButtonPrimary>
      </Ready>
    </DatePickerBase>
  )
}

DatePicker.propTypes = {
  checkIn: PropTypes.instanceOf(Date).isRequired,
  checkOut: PropTypes.instanceOf(Date).isRequired,
  dataTestId: PropTypes.string,
  initialFocusedInput: PropTypes.string,
  onChangeDates: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  market: PropTypes.string,
  type: PropTypes.oneOf(['default', 'availability']),
  currentDate: PropTypes.instanceOf(Date),
  unFreezeScroll: PropTypes.bool,
}

export default DatePicker
